import React, { useCallback, useEffect, useMemo, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";

type UserRoleResponse = {
  ok?: boolean;
  rol?: string;
  data?: {
    id?: number | string;
    user_id?: number | string;
  };
  error?: string;
};

type LegacyRow = Record<string, unknown>;

type LegacySellerResponse = {
  ok?: boolean;
  result?: LegacyRow[];
  error?: string;
};

type StoreRaw = Record<string, unknown>;

type SellerStoresResponse = {
  ok?: boolean;
  stores?: StoreRaw[];
  data?: StoreRaw[];
  result?: StoreRaw[];
  error?: string;
};

type SellerStore = {
  id: number | string | null;
  fantasyName: string;
  legacyName: string;
};

type SellerDeliveryItem = {
  id: string;
  tracking: string;
  fechaColecta: string;
  destinatario: string;
  direccion: string;
  cp: string;
  localidad: string;
  provincia: string;
  zona: string;
  metodoEnvio: string;
  cadete: string;
  total: string;
  precioCliente: number;
  nombreFantasia: string;
  legacyName: string;
  estado: string;
};

const PAGE_SIZE = 10;

const pageStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
};

const topbarStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "10px",
  flexWrap: "wrap",
};

const actionsStyle: React.CSSProperties = {
  display: "flex",
  gap: "8px",
  flexWrap: "wrap",
  alignItems: "center",
};

const selectLabelStyle: React.CSSProperties = {
  fontSize: "13px",
  fontWeight: 700,
  color: "var(--text-secondary)",
};

const selectStyle: React.CSSProperties = {
  minWidth: "210px",
  height: "38px",
  borderRadius: "10px",
  border: "1px solid var(--border)",
  background: "var(--surface)",
  color: "var(--text-primary)",
  padding: "0 12px",
  fontSize: "13px",
  fontWeight: 700,
  outline: "none",
};

const filtersCardStyle: React.CSSProperties = {
  padding: "14px",
};

const filterGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
  gap: "10px",
  alignItems: "end",
};

const filterActionsStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "10px",
  marginTop: "10px",
  flexWrap: "wrap",
};

const tableCardStyle: React.CSSProperties = {
  padding: "12px",
};

const tableWrapStyle: React.CSSProperties = {
  overflowX: "auto",
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "9px 10px",
  color: "var(--text-secondary)",
  fontSize: "12px",
  fontWeight: 800,
  borderBottom: "1px solid var(--border)",
  whiteSpace: "nowrap",
};

const tdStyle: React.CSSProperties = {
  padding: "9px 10px",
  color: "var(--text-primary)",
  fontSize: "13px",
  borderBottom: "1px solid var(--border)",
  verticalAlign: "middle",
};

const emptyStateStyle: React.CSSProperties = {
  padding: "24px 16px",
  borderRadius: "12px",
  border: "1px dashed var(--border)",
  textAlign: "center",
  color: "var(--text-secondary)",
  fontSize: "13px",
};

const metaTextStyle: React.CSSProperties = {
  fontSize: "11px",
  color: "var(--text-secondary)",
  marginTop: "2px",
};

const badgeStyle = (tone: "ok" | "warning" | "neutral"): React.CSSProperties => {
  const background =
    tone === "ok"
      ? "rgba(34,197,94,0.12)"
      : tone === "warning"
      ? "rgba(245,158,11,0.14)"
      : "rgba(255,255,255,0.06)";

  const color =
    tone === "ok" ? "#22c55e" : tone === "warning" ? "#f59e0b" : "var(--text-primary)";

  return {
    minHeight: "28px",
    padding: "0 10px",
    borderRadius: "10px",
    border: "1px solid var(--border)",
    background,
    color,
    display: "inline-flex",
    alignItems: "center",
    fontSize: "11px",
    fontWeight: 800,
    whiteSpace: "nowrap",
  };
};

const paginationStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "10px",
  paddingTop: "10px",
  flexWrap: "wrap",
};

const paginationTextStyle: React.CSSProperties = {
  fontSize: "12px",
  color: "var(--text-secondary)",
};

const safeString = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const resolveStatus = (row: LegacyRow): string =>
  safeString(row.status_ml) || safeString(row.status) || safeString(row.estado) || "Sin estado";

const resolveTracking = (row: LegacyRow): string =>
  safeString(row.tracking) ||
  safeString(row.numero_tracking) ||
  safeString(row.id_venta_ml) ||
  "—";

const resolveBuyer = (row: LegacyRow): string =>
  safeString(row.nombre_destinatario) ||
  safeString(row.buyer_nickname) ||
  safeString(row.buyer_name) ||
  "Sin destinatario";

const resolveAddress = (row: LegacyRow): string =>
  safeString(row.direccion) || safeString(row.address) || "Sin dirección";

const resolveStatusTone = (status: string): "ok" | "warning" | "neutral" => {
  const normalized = status.toLowerCase();

  if (normalized.includes("entregado") || normalized.includes("delivered")) return "ok";

  if (
    normalized.includes("demorado") ||
    normalized.includes("reprogramado") ||
    normalized.includes("nadie") ||
    normalized.includes("pendiente")
  ) {
    return "warning";
  }

  return "neutral";
};

const toNumber = (value: unknown): number => {
  if (typeof value === "number") return value;

  if (typeof value === "string") {
    const normalized = value.replace(/\./g, "").replace(",", ".");
    const parsed = Number(normalized);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  return 0;
};

const pickStoresArray = (payload: SellerStoresResponse): StoreRaw[] => {
  if (Array.isArray(payload?.stores)) return payload.stores;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.result)) return payload.result;
  return [];
};

const normalizeStore = (store: StoreRaw): SellerStore => {
  const id =
    (store.id as number | string | null | undefined) ??
    (store.store_id as number | string | null | undefined) ??
    null;

  const fantasyName =
    safeString(store.nombre_fantasia) ||
    safeString(store.nombreFantasia) ||
    safeString(store.name) ||
    safeString(store.store_name);

  const legacyName =
    safeString(store.legacy_fantasy_name) ||
    safeString(store.legacyFantasyName) ||
    safeString(store.legacy_name) ||
    fantasyName;

  return {
    id,
    fantasyName,
    legacyName,
  };
};

const mapLegacyRowToItem = (
  row: LegacyRow,
  index: number,
  store: SellerStore
): SellerDeliveryItem => ({
  id: `${resolveTracking(row)}-${store.id ?? store.legacyName}-${index}`,
  tracking: resolveTracking(row),
  fechaColecta: safeString(row.fecha_colecta),
  destinatario: resolveBuyer(row),
  direccion: resolveAddress(row),
  cp: safeString(row.cp),
  localidad: safeString(row.localidad),
  provincia: safeString(row.provincia),
  zona: safeString(row.zona),
  metodoEnvio: safeString(row.metodo_envio),
  cadete: safeString(row.cadete),
  total: safeString(row.total),
  precioCliente: toNumber(row.precio_cliente),
  nombreFantasia: store.fantasyName || safeString(row.nombre_fantasia) || "—",
  legacyName: store.legacyName,
  estado: resolveStatus(row),
});

const uniqueRowsByTracking = (rows: SellerDeliveryItem[]): SellerDeliveryItem[] => {
  const seen = new Set<string>();

  return rows.filter((row, index) => {
    const key = row.tracking && row.tracking !== "—" ? row.tracking : `${row.id}-${index}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export default function SellerDeliveries() {
  const { token, userId, username } = useAuth();

  const [stores, setStores] = useState<SellerStore[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string>("all");
  const [rows, setRows] = useState<SellerDeliveryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const [trackingQuery, setTrackingQuery] = useState<string>("");
  const [recipientQuery, setRecipientQuery] = useState<string>("");
  const [statusQuery, setStatusQuery] = useState<string>("");
  const [localityQuery, setLocalityQuery] = useState<string>("");
  const [zoneQuery, setZoneQuery] = useState<string>("");
  const [driverQuery, setDriverQuery] = useState<string>("");
  const [shippingMethodQuery, setShippingMethodQuery] = useState<string>("");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [moreFiltersOpen, setMoreFiltersOpen] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);

  const selectedStore = useMemo(() => {
    if (selectedStoreId === "all") return null;
    return stores.find((store) => String(store.id) === selectedStoreId) || null;
  }, [selectedStoreId, stores]);

  const visibleRows = useMemo(() => {
    if (!selectedStore) return rows;
    return rows.filter((row) => row.nombreFantasia === selectedStore.fantasyName);
  }, [rows, selectedStore]);

  const primaryLegacyName = useMemo(() => {
    if (selectedStore) return selectedStore.legacyName;
    return stores[0]?.legacyName || "";
  }, [selectedStore, stores]);

  const fetchSellerRows = useCallback(async () => {
    if (!token || !userId || !username) {
      setError("No se pudo identificar el seller autenticado.");
      setLoading(false);
      setRefreshing(false);
      return;
    }

    try {
      setError("");

      const roleResponse = await authFetch(`/v2/users/${userId}/role`, { token });
      const rolePayload = (await roleResponse
        .json()
        .catch(() => ({}))) as UserRoleResponse;

      if (!roleResponse.ok || rolePayload?.ok === false) {
        throw new Error(
          rolePayload?.error || "No se pudo resolver el seller asociado al usuario."
        );
      }

      const sellerId = rolePayload?.data?.id;

      if (!sellerId) {
        throw new Error("El usuario no tiene seller_data asociado.");
      }

      const storesResponse = await authFetch(`/v2/sellers/${sellerId}/store`, {
        token,
      });

      const storesPayload = (await storesResponse
        .json()
        .catch(() => ({}))) as SellerStoresResponse;

      if (!storesResponse.ok || storesPayload?.ok === false) {
        throw new Error(
          storesPayload?.error ||
            "No se pudieron cargar las tiendas asociadas al seller."
        );
      }

      const parsedStores = pickStoresArray(storesPayload)
        .map(normalizeStore)
        .filter((store) => Boolean(store.fantasyName || store.legacyName));

      setStores(parsedStores);

      if (!parsedStores.length) {
        setRows([]);
        setSelectedStoreId("all");
        setError(
          "Este seller no tiene tiendas asociadas. Asociá un store antes de consultar legacy."
        );
        return;
      }

      const collectedRows: SellerDeliveryItem[] = [];

      for (const store of parsedStores) {
        if (!store.legacyName) continue;

        const legacyResponse = await authFetch("/v1/data/client/me", {
          method: "POST",
          token,
          json: true,
          body: JSON.stringify({
            username: store.legacyName,
          }),
        });

        const legacyPayload = (await legacyResponse
          .json()
          .catch(() => ({}))) as LegacySellerResponse;

        if (!legacyResponse.ok || legacyPayload?.ok === false) {
          throw new Error(
            legacyPayload?.error ||
              `No se pudieron obtener los envíos legacy para ${store.legacyName}.`
          );
        }

        const legacyRows = Array.isArray(legacyPayload.result)
          ? legacyPayload.result
          : [];

        legacyRows.forEach((row, index) => {
          collectedRows.push(mapLegacyRowToItem(row, index, store));
        });
      }

      setRows(uniqueRowsByTracking(collectedRows));

      if (
        selectedStoreId !== "all" &&
        !parsedStores.some((store) => String(store.id) === selectedStoreId)
      ) {
        setSelectedStoreId("all");
      }
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : "Error cargando envíos.");
      setRows([]);
      setStores([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, userId, username, selectedStoreId]);

  useEffect(() => {
    fetchSellerRows();
  }, [fetchSellerRows]);

  const filteredRows = useMemo(() => {
    return visibleRows.filter((row) => {
      const matchesTracking =
        !trackingQuery ||
        row.tracking.toLowerCase().includes(trackingQuery.toLowerCase());

      const matchesRecipient =
        !recipientQuery ||
        row.destinatario.toLowerCase().includes(recipientQuery.toLowerCase());

      const matchesStatus =
        !statusQuery ||
        row.estado.toLowerCase().includes(statusQuery.toLowerCase());

      const matchesLocality =
        !localityQuery ||
        row.localidad.toLowerCase().includes(localityQuery.toLowerCase());

      const matchesZone =
        !zoneQuery || row.zona.toLowerCase().includes(zoneQuery.toLowerCase());

      const matchesDriver =
        !driverQuery || row.cadete.toLowerCase().includes(driverQuery.toLowerCase());

      const matchesShippingMethod =
        !shippingMethodQuery ||
        row.metodoEnvio.toLowerCase().includes(shippingMethodQuery.toLowerCase());

      const matchesDateFrom = !dateFrom || !row.fechaColecta || row.fechaColecta >= dateFrom;
      const matchesDateTo = !dateTo || !row.fechaColecta || row.fechaColecta <= dateTo;

      return (
        matchesTracking &&
        matchesRecipient &&
        matchesStatus &&
        matchesLocality &&
        matchesZone &&
        matchesDriver &&
        matchesShippingMethod &&
        matchesDateFrom &&
        matchesDateTo
      );
    });
  }, [
    visibleRows,
    trackingQuery,
    recipientQuery,
    statusQuery,
    localityQuery,
    zoneQuery,
    driverQuery,
    shippingMethodQuery,
    dateFrom,
    dateTo,
  ]);

  useEffect(() => {
    setPage(1);
  }, [
    selectedStoreId,
    trackingQuery,
    recipientQuery,
    statusQuery,
    localityQuery,
    zoneQuery,
    driverQuery,
    shippingMethodQuery,
    dateFrom,
    dateTo,
  ]);

  const totalPages = Math.max(Math.ceil(filteredRows.length / PAGE_SIZE), 1);

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredRows.slice(start, start + PAGE_SIZE);
  }, [filteredRows, page]);

  const clearFilters = () => {
    setTrackingQuery("");
    setRecipientQuery("");
    setStatusQuery("");
    setLocalityQuery("");
    setZoneQuery("");
    setDriverQuery("");
    setShippingMethodQuery("");
    setDateFrom("");
    setDateTo("");
  };

  const handleDownloadExcel = async () => {
    if (!token) return;

    if (!primaryLegacyName) {
      alert("No hay una tienda asociada para descargar.");
      return;
    }

    try {
      setDownloading(true);

      const response = await authFetch("/v1/data/client/print", {
        method: "POST",
        token,
        json: true,
        body: JSON.stringify({
          username: primaryLegacyName,
        }),
      });

      if (!response.ok) {
        const maybeJson = await response.json().catch(() => null);
        throw new Error(maybeJson?.error || "No se pudo descargar el Excel.");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const safeFileName = primaryLegacyName.replace(/\s+/g, "_").toLowerCase();

      link.href = url;
      link.download = `envios_${safeFileName}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert(err instanceof Error ? err.message : "Error descargando Excel.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={pageStyle}>
      <section style={topbarStyle}>
        <div style={actionsStyle}>
          <span style={selectLabelStyle}>Ver por tienda</span>

          <select
            style={selectStyle}
            value={selectedStoreId}
            onChange={(event) => setSelectedStoreId(event.target.value)}
            disabled={loading || !stores.length}
          >
            <option value="all">Todas las tiendas</option>
            {stores.map((store) => (
              <option key={String(store.id)} value={String(store.id)}>
                {store.fantasyName}
              </option>
            ))}
          </select>
        </div>

        <div style={actionsStyle}>
          <Button
            variant="secondary"
            onClick={() => {
              setRefreshing(true);
              fetchSellerRows();
            }}
            disabled={refreshing}
          >
            {refreshing ? "Actualizando..." : "Actualizar"}
          </Button>

          <Button
            onClick={handleDownloadExcel}
            disabled={downloading || loading || !primaryLegacyName}
          >
            {downloading ? "Descargando..." : "Descargar Excel"}
          </Button>
        </div>
      </section>

      <Card style={filtersCardStyle}>
        <div style={filterGridStyle}>
          <Input
            label="Tracking"
            placeholder="Tracking"
            value={trackingQuery}
            onChange={(event) => setTrackingQuery(event.target.value)}
          />

          <Input
            label="Destinatario"
            placeholder="Nombre"
            value={recipientQuery}
            onChange={(event) => setRecipientQuery(event.target.value)}
          />

          <Input
            label="Estado"
            placeholder="Estado"
            value={statusQuery}
            onChange={(event) => setStatusQuery(event.target.value)}
          />

          <Input
            label="Localidad"
            placeholder="Localidad"
            value={localityQuery}
            onChange={(event) => setLocalityQuery(event.target.value)}
          />

          <Input
            label="Zona"
            placeholder="Zona"
            value={zoneQuery}
            onChange={(event) => setZoneQuery(event.target.value)}
          />
        </div>

        {moreFiltersOpen ? (
          <div style={{ ...filterGridStyle, marginTop: "10px" }}>
            <Input
              label="Cadete"
              placeholder="Chofer"
              value={driverQuery}
              onChange={(event) => setDriverQuery(event.target.value)}
            />

            <Input
              label="Método"
              placeholder="Método"
              value={shippingMethodQuery}
              onChange={(event) => setShippingMethodQuery(event.target.value)}
            />

            <Input
              label="Desde"
              type="date"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
            />

            <Input
              label="Hasta"
              type="date"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
            />

            <div />
          </div>
        ) : null}

        <div style={filterActionsStyle}>
          <span style={paginationTextStyle}>
            {filteredRows.length} envíos encontrados
          </span>

          <div style={actionsStyle}>
            <Button
              variant="ghost"
              onClick={() => setMoreFiltersOpen((current) => !current)}
            >
              {moreFiltersOpen ? "Menos filtros" : "Más filtros"}
            </Button>

            <Button variant="ghost" onClick={clearFilters}>
              Limpiar filtros
            </Button>
          </div>
        </div>
      </Card>

      <Card style={tableCardStyle}>
        {loading ? (
          <div style={emptyStateStyle}>Cargando envíos...</div>
        ) : error ? (
          <div style={emptyStateStyle}>{error}</div>
        ) : filteredRows.length === 0 ? (
          <div style={emptyStateStyle}>
            No hay envíos para mostrar con los filtros actuales.
          </div>
        ) : (
          <>
            <div style={tableWrapStyle}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Tracking</th>
                    <th style={thStyle}>Destinatario</th>
                    <th style={thStyle}>Dirección</th>
                    <th style={thStyle}>Zona</th>
                    <th style={thStyle}>Método</th>
                    <th style={thStyle}>Cadete</th>
                    <th style={thStyle}>Estado</th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedRows.map((row) => (
                    <tr key={row.id}>
                      <td style={tdStyle}>
                        <div>{row.tracking}</div>
                        <div style={metaTextStyle}>{row.fechaColecta || "Sin fecha"}</div>
                      </td>

                      <td style={tdStyle}>{row.destinatario}</td>

                      <td style={tdStyle}>
                        <div>{row.direccion}</div>
                        <div style={metaTextStyle}>
                          {[row.localidad, row.provincia, row.cp].filter(Boolean).join(" · ")}
                        </div>
                      </td>

                      <td style={tdStyle}>
                        <div>{row.zona || "—"}</div>
                        <div style={metaTextStyle}>{row.nombreFantasia}</div>
                      </td>

                      <td style={tdStyle}>
                        <div>{row.metodoEnvio || "—"}</div>
                        <div style={metaTextStyle}>{row.total || "—"}</div>
                      </td>

                      <td style={tdStyle}>{row.cadete || "—"}</td>

                      <td style={tdStyle}>
                        <span style={badgeStyle(resolveStatusTone(row.estado))}>
                          {row.estado}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={paginationStyle}>
              <span style={paginationTextStyle}>
                Página {page} de {totalPages} · mostrando {paginatedRows.length} de{" "}
                {filteredRows.length}
              </span>

              <div style={actionsStyle}>
                <Button
                  variant="ghost"
                  onClick={() => setPage((current) => Math.max(current - 1, 1))}
                  disabled={page <= 1}
                >
                  Anterior
                </Button>

                <Button
                  variant="secondary"
                  onClick={() =>
                    setPage((current) => Math.min(current + 1, totalPages))
                  }
                  disabled={page >= totalPages}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}